import { FC, useEffect, useMemo, useRef, useState } from 'react'
import TooltipMaxLength from '../../../hooks/utils/TooltipMaxLength'
import Pagination from '../../components/Pagination'
import { defaultProfileImage } from '../../../utils/constants'
import { getTasks } from '../../../services/adminTicketsService'

type Props = {
    userId?: number
}

type ApiTaskUser = {
    id: number
    firstName?: string
    lastName?: string
    email?: string
}

type ApiTaskExperience = {
    id: number
    title?: string
}

type ApiTask = {
    id: number
    title?: string
    notes?: string
    dueDate?: string | null
    createdAt?: string | null
    status?: string
    statusDisplay?: string
    createdBy?: number | null
    createdByName?: string | null
    relatedMembers?: ApiTaskUser[]
    relatedPartners?: ApiTaskUser[]
    relatedExperiences?: ApiTaskExperience[]
}

const LIMIT = 10

const STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'cancelled', label: 'Cancelled' },
]

const STATUS_CLASS: Record<string, string> = {
    pending: 'support-tickets-status--pending',
    in_progress: 'support-tickets-status--in-progress',
    resolved: 'support-tickets-status--resolved',
    cancelled: 'support-tickets-status--cancelled',
}

const formatDate = (iso?: string | null) => {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: '2-digit',
        })
    } catch {
        return iso
    }
}

const userLabel = (u: ApiTaskUser) => {
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
    return name || u.email || `#${u.id}`
}

const relatedItemSummary = (t: ApiTask): string => {
    const members = (t.relatedMembers ?? []).map(userLabel)
    const partners = (t.relatedPartners ?? []).map(userLabel)
    const experiences = (t.relatedExperiences ?? []).map((e) => e.title ?? `#${e.id}`)
    const parts: string[] = []
    if (experiences.length) parts.push(`Experience: ${experiences.join(', ')}`)
    if (partners.length) parts.push(`Partner: ${partners.join(', ')}`)
    if (members.length) parts.push(`Member: ${members.join(', ')}`)
    return parts.join(' · ') || '—'
}

export const SupportTicketWrapper: FC<Props> = ({ userId }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [count, setCount] = useState<number>(0)
    const [tasks, setTasks] = useState<ApiTask[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [status, setStatus] = useState<string>('')
    const [search, setSearch] = useState<string>('')
    const [debouncedSearch, setDebouncedSearch] = useState<string>('')
    const totalPages = useMemo(() => (count ? Math.ceil(count / LIMIT) : 1), [count])

    const reqRef = useRef(0)

    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 300)
        return () => clearTimeout(id)
    }, [search])

    const fetchTasks = async (page: number) => {
        if (!userId) return
        const reqId = ++reqRef.current
        setLoading(true)
        try {
            const offset = (page - 1) * LIMIT
            const params: Record<string, any> = { member: userId, limit: LIMIT, offset }
            if (status) params.status = status
            if (debouncedSearch) params.search = debouncedSearch
            const res: any = await getTasks(params)
            if (reqId !== reqRef.current) return
            setTasks(res?.results ?? [])
            setCount(Number(res?.count ?? 0))
        } catch {
            if (reqId === reqRef.current) {
                setTasks([])
                setCount(0)
            }
        } finally {
            if (reqId === reqRef.current) setLoading(false)
        }
    }

    useEffect(() => {
        setCurrentPage(1)
        fetchTasks(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, status, debouncedSearch])

    const handlePageChange = async (page: number) => {
        setCurrentPage(page)
        await fetchTasks(page)
    }

    return (
        <div className="financial-page-admin support-tickets-panel">
            <div className="financial-membership">
                <div className="financial-membership__container support-tickets-panel__container">
                    <div className="support-tickets-filters">
                        <div className="support-tickets-filters__search">
                            <input
                                type="text"
                                placeholder="Search by title or note…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="support-tickets-filters__chips" role="tablist" aria-label="Filter by status">
                            {STATUS_OPTIONS.map((opt) => (
                                <button
                                    type="button"
                                    key={opt.value || 'all'}
                                    role="tab"
                                    aria-selected={status === opt.value}
                                    className={`support-tickets-filters__chip${status === opt.value ? ' support-tickets-filters__chip--active' : ''}`}
                                    onClick={() => setStatus(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {count > 0 && (
                            <span className="support-tickets-filters__count">
                                {count} task{count === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>

                    <div className="support-tickets-table">
                        <div className="support-tickets-table__header">
                            <div>Date</div>
                            <div>Status</div>
                            <div>Note</div>
                            <div>Related item</div>
                            <div>Nobilis admin</div>
                        </div>

                        {tasks.map((t) => {
                            const statusClass = t.status ? STATUS_CLASS[t.status] ?? '' : ''
                            return (
                                <div key={t.id} className="support-tickets-table__row">
                                    <div className="support-tickets-table__cell">
                                        {formatDate(t.dueDate ?? t.createdAt)}
                                    </div>
                                    <div className="support-tickets-table__cell">
                                        <span className={`support-tickets-status ${statusClass}`}>
                                            {t.statusDisplay ?? t.status ?? '—'}
                                        </span>
                                    </div>
                                    <div className="support-tickets-table__cell">
                                        <TooltipMaxLength
                                            text={t.title ? `${t.title}${t.notes ? ' — ' + t.notes : ''}` : (t.notes ?? '—')}
                                            maxLength={120}
                                            maxWidth={420}
                                            position="bottom"
                                        />
                                    </div>
                                    <div className="support-tickets-table__cell">
                                        <TooltipMaxLength
                                            text={relatedItemSummary(t)}
                                            maxLength={70}
                                            maxWidth={360}
                                            position="bottom"
                                        />
                                    </div>
                                    <div className="support-tickets-table__cell card-profile-avatar">
                                        <img
                                            src={defaultProfileImage}
                                            alt={t.createdByName ?? ''}
                                            onError={(e) => { e.currentTarget.src = defaultProfileImage }}
                                        />
                                        <span>{t.createdByName ?? '—'}</span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className='tap-flex-center w-100 mt-4'>
                    {tasks.length > 0 && totalPages > 1 && (
                        <div className='tap-flex-center mt-5'>
                            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                        </div>
                    )}
                    {!loading && tasks.length === 0 && (
                        <div className="no-introductions">
                            {status || debouncedSearch
                                ? 'No tasks match the current filters.'
                                : 'No tasks linked to this member yet.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export interface Ticket {
  id: number;
  date: string;
  type: string;
  note: string;
  relatedItem: string;
  admin: {
    name: string;
    avatar: string;
  };
}
